<template>
  <div class="array-table-card" data-id="mpower-table-card">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="d-flex align-items-center">
        <span :class="[headingClass]"
          ><i :class="`far fa-${icon}`" v-if="icon" /> {{ heading }} {{ paginate ? `Page: ${currentPage}` : '' }}</span
        >
        <slot :name="'heading'" />
      </div>

      <div>
        <div v-if="!disabled" class="btn btn-link" @click="addNew" data-id="mpower-table-card-add">
          <i class="far fa-plus"></i> Add {{ heading }}
        </div>
      </div>
    </div>
    <table class="table table-sm" v-if="value.length > 0">
      <thead>
        <tr>
          <th v-for="(column, index) in columns" :key="column.title">{{ column.title }}</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody :key="updatedAt">
        <tr
          class="item-record"
          v-for="(item, index) in paginatedArray"
          :key="item.id || item._id || getItemIndex(index)"
          :data-id="`mpower-table-card-record-${item._id || index}`"
        >
          <td v-for="column in columns" :key="column.title" class="py-2">
            <slot :item="item" :column="column" :name="column.label" :index="getItemIndex(index)"></slot>
          </td>
          <td class="py-2">
            <div class="d-flex align-items-end flex-column" v-if="!disabled">
              <div class="d-flex align-items-center">
                <button class="btn btn-link mr-1" @click="clonePart(item)" v-if="duplicate">
                  <i class="far fa-clone"></i>
                </button>
                <button class="btn btn-link mr-1" @click="deleteItem(index)">
                  <i class="far fa-trash-alt" title="Remove record" v-b-tooltip.hover></i>
                </button>
                <button class="btn btn-link mr-1" @click="() => move(index, 1)" v-if="canMove">
                  <i class="far fa-long-arrow-down" title="Move down" v-b-tooltip.hover></i>
                </button>
                <button class="btn btn-link" mr-1 @click="() => move(index, -1)" v-if="canMove">
                  <i class="far fa-long-arrow-up" title="Move up" v-b-tooltip.hover></i>
                </button>
                <b-form-checkbox
                  v-b-tooltip.hover
                  :title="'Show only this record'"
                  v-if="canMarkOnly"
                  v-model="item.showOnly"
                  @change="() => onShowOnly(item)"
                >
                </b-form-checkbox>
              </div>
              <div v-if="canMarkOnly" class="show-only">Show only</div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <b-modal ref="deleteConfirm" :title="`Delete ${heading}`" @ok="onDeleteConfirm" @close="closeDeleteModal">
      <p class="mb-0">Are you sure you want to remove record?</p>
    </b-modal>
    <b-pagination-nav
      v-model="currentPage"
      align="center"
      base-url="#"
      :number-of-pages="totalRows"
      :per-page="perPage"
      @change="scrollToTop"
      v-if="paginate"
    ></b-pagination-nav>
  </div>
</template>
<script>
export default {
  inject: ['$validator'],
  name: 'ArrayTableCard',
  props: {
    canMarkOnly: {
      type: Boolean
    },
    columns: {
      type: Array,
      default: () => []
    },
    cardSize: {
      type: Number,
      default: 12
    },
    icon: {
      type: String
    },
    heading: {
      type: String,
      required: true
    },
    canMove: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    headingClass: {
      type: String,
      default: () => 'h6'
    },
    value: {
      type: Array,
      required: true,
      default: () => []
    },
    defaultItem: {
      type: Object | Function,
      required: true
    },
    paginate: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 10
    },
    duplicate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      updatedAt: 1,
      indexToDelete: -1,
      currentPage: 1
    };
  },
  computed: {
    totalRows() {
      return Math.ceil(this.value.length / this.perPage);
    },
    paginatedArray() {
      if (this.paginate) {
        return [...this.value].slice((this.currentPage - 1) * this.perPage, this.perPage * this.currentPage);
      }
      return this.value;
    },
    headingPluralized() {
      if (this.heading.slice(-1) === 's') {
        return `${this.heading}es`;
      }
      return `${this.heading}s`;
    }
  },
  methods: {
    onShowOnly(item) {
      const showOnly = item.showOnly;
      this.value.forEach((item, index) => {
        this.$set(item, 'showOnly', false);
      });
      item.showOnly = showOnly;
      this.updatedAt = Date.now();
    },
    move(_index, dir) {
      const index = this.getItemIndex(_index);
      const item = this.value.splice(index, 1)[0];
      let newIndex = index + dir;
      if (newIndex < 0) newIndex = this.value.length;
      if (newIndex > this.value.length) newIndex = 0;
      this.value.splice(newIndex, 0, item);
      this.$forceUpdate();
      this.$emit('change', this.value);
    },
    addNew() {
      const newRecord = Object.assign(
        {},
        (typeof this.defaultItem === 'function' ? this.defaultItem() : this.defaultItem) || {}
      );
      this.value.push(newRecord);
      this.$emit('add', newRecord);
    },
    getItemIndex(index) {
      return this.currentPage === 1 ? index : (this.currentPage - 1) * this.perPage + index;
    },
    closeDeleteModal() {
      this.$refs.deleteConfirm.hide();
      this.indexToDelete = -1;
    },
    onDeleteConfirm() {
      const itemIndex = this.getItemIndex(this.indexToDelete);
      this.$emit('delete', this.value[itemIndex]);
      this.value.splice(itemIndex, 1);
      this.currentPage = Math.ceil(this.value.length / this.perPage);
      this.closeDeleteModal();
    },
    deleteItem(index) {
      this.$refs.deleteConfirm.show();
      this.indexToDelete = index;
    },
    scrollToTop(page) {
      window.scrollTo(0, 0);
    },
    clonePart(item) {
      try {
        const model = JSON.parse(JSON.stringify(item));
        delete model._id;
        this.value.push(model);

        setTimeout(() => (this.currentPage = this.value.length));
      } catch (e) {
        this.notificateError('Clone failed');
      }
    }
  }
};
</script>
<style lang="scss">
.array-table-card {
  fieldset {
    &.form-group {
      margin: 0;
      label {
        display: none;
      }
    }
  }
  .item-record {
    .show-only {
      visibility: hidden;
    }
    &:hover {
      .show-only {
        visibility: visible;
      }
    }
  }
}
</style>
